<template>
  <div class="product-tile" data-cy="product-tile">
    <header class="product-item__header">
      <span class="product-name">
        <div @click="onTileClick">
          <span class="product-tile__title" data-cy="product-tile-title">
            {{ product.LabelName }}
            {{ product.LabelNum ? "/ " + product.LabelNum : "" }}</span
          >
          <span class="label-dpm" data-cy="product-tile-dpm"
            >[{{ product.Dpm }}]</span
          >
        </div>
        <span class="task-list__card-info task-list__card-info--label">
          <div @click="onTileClick">
            <div data-cy="product-tile-company">{{ product.CompanyName }}</div>
            <div data-cy="product-tile-brand">{{ product.BrandName }}</div>
            <div data-cy="product-tile-agency">
              {{
                product.VueFullUkdId?.length
                  ? product.VueFullUkdId
                  : product.AgencyId
              }}
            </div>
            <div data-cy="product-tile-symbol">{{ product.Symbol }}</div>
            <div class="special-req-container">
              <span
                v-if="product.Pesach == 'Y'"
                class="special-req-indicator"
                data-cy="product-tile-passover"
                >Passover</span
              >
              <span
                v-if="product.Comments == 'Y' || product.Conditions?.length"
                class="special-req-indicator"
                data-cy="product-tile-comments"
                >Special Requirements</span
              >
            </div>
          </div>
          <div class="loc-download-container">
            <a
              v-if="product.IsOkToPrintLoc"
              class="loc-dwnld"
              @click="downloadSingleLoc(product.VueFullUkdId)"
              data-cy="product-tile-loc"
            >
              <DownloadIcon />
              Product LOC
            </a>

            <a
              v-if="product.isOkToPrintCompanyLoc"
              class="loc-dwnld"
              @click="downloadLoc(product.VueFullUkdId)"
              data-cy="product-tile-company-loc"
            >
              <DownloadIcon />
              Company LOC
            </a>
          </div>
        </span>
      </span>
    </header>

    <div v-show="showDetails" class="product-details" data-cy="product-details">
      <Grid
        :data-items="productProperties"
        :columns="columns"
        :scrollable="'none'"
      >
        <template v-slot:emptyHeader="{}"> </template>
      </Grid>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { OuKashUnionUkdProducts } from "@/Domain/OuKashUnionUkdProducts";
import { ref, watchEffect } from "vue";
import { getCertificationEndDateString } from "@/shared/helpers/productsHelper";
import { Grid, type GridColumnProps } from "@progress/kendo-vue-grid";
import DownloadIcon from "@/icons/DownloadIcon.vue";
import {
  printLocAsync,
  printLocUkdSingleProductAsync,
} from "@/shared/helpers/printLocHelper";
import type { LocPrintRequest } from "@/Domain/LocPrintRequest";
import { useMainStore } from "@/stores/useMainStore";

const main = useMainStore();

const props = defineProps<{
  product: OuKashUnionUkdProducts;
}>();

const showDetails = ref<boolean>(false);
const productProperties = ref<{ name: string; value: string }[]>([]);

const columns: GridColumnProps[] = [
  {
    field: "name",
    headerCell: "emptyHeader",
    className: "name-row",
  },
  {
    field: "value",
    headerCell: "emptyHeader",
  },
];

const setProductProperties = function () {
  productProperties.value = [
    {
      name: "Passover",
      value: props.product.Pesach,
    },
    {
      name: "Passover Symbol",
      value: props.product.PassoverSymbol,
    },
    {
      name: "Agency Name",
      value: props.product.AgencyName,
    },
    {
      name: "Consumer / Industrial",
      value: props.product.CI,
    },
    {
      name: "Conditions",
      value: props.product.Conditions,
    },
    {
      name: "Certified Until",
      value: getCertificationEndDateString(props.product),
    },
    {
      name: "Web Site",
      value: props.product.WebSite,
    },
    {
      name: "Location",
      value: `${props.product.CompanyCity}, ${props.product.CompanyState}, ${props.product.CompanyCountry}`,
    },
    {
      name: "Company Rabbi",
      value: props.product.CompanyRc
        ? `${props.product.CompanyRc}-${props.product.CompanyRcEmail}`
        : "",
    },
  ];
};

setProductProperties();

const onTileClick = function (e: MouseEvent) {
  const el = e.target as Element;
  if (el && el.tagName.toLowerCase() === "a") return;
  showDetails.value = !showDetails.value;
};

const downloadLoc = (id: string) => {
  printLocAsync({
    CompanyLogin: main.companyLogin,
    LocReportIsFullLoc: true,
    UkdId: id,
    LocCalledFrom: "~/UkdProductList.aspx",
  } as LocPrintRequest);
};

const downloadSingleLoc = (id: string) => {
  printLocUkdSingleProductAsync({
    UkdId: id,
  });
};

watchEffect(() => {
  setProductProperties();
});
</script>

<style lang="scss">
.product-tile {
  background: #fff;
  margin-bottom: 10px;
  border-radius: 4px;
  padding: 15px;
  cursor: pointer;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  display: block;

  &.__title {
    font: 16px/1.5 proxima-nova, sans-serif;
    color: #444955;
    background-color: #fff;
    font-weight: 400;
    -moz-osx-font-smoothing: grayscale;
    -webkit-font-smoothing: antialiased;
  }

  .label-dpm {
    display: inline-block;
    font-size: 12px;
    margin-left: 5px;
    color: #999;
  }

  .task-list__card-info--label {
    color: #a5a5a5;
    font-size: 12px;
    margin-top: 6px;
    display: block;

    .loc-download-container {
      display: flex;
      gap: 12px;
      margin-top: 12px;
    }

    .loc-dwnld {
      display: inline-flex;
      color: white;
      background: #33669a;
      padding: 5px 15px;
      align-items: center;
      text-decoration: none;

      svg {
        margin-right: 5px;
        fill: white;
      }
    }
  }

  .special-req-container {
    display: flex;
    gap: 12px;
  }

  .special-req-indicator {
    display: inline-block;
    margin-top: 5px;
    background: #666;
    color: white;
    font-size: 11px;
    padding: 3px 7px;
  }

  .product-details {
    margin-top: 12px;

    .k-grid {
      border: 0;
    }

    .k-grid-header {
      display: none !important;
    }
  }
}
</style>
